/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import urlJoin from 'proper-url-join';
import React, { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../store';
import InstancesComponent from '../../components/instances/Instances';
import { loadInstances } from '../../store/instances/actions';
import {
  areInstancesLoading,
  areInstancesUpdating,
  selectAllInstances,
  selectCachedInstances
} from '../../store/instances/selectors';
import { updateTenant } from '../../store/session/actions';
import history from '../../util/history';
import { pageEvents } from '../../util/trackers/segment';

import './Instances.scss';

const InstancesPage: React.FC = () => {
  const dispatch = useAppDispatch();

  const loading = useAppSelector(areInstancesLoading);
  const updating = useAppSelector(areInstancesUpdating);
  const instances = useAppSelector(selectAllInstances);

  const actualInstances =
    loading || updating ? selectCachedInstances() : instances;

  useEffect(() => {
    pageEvents.instances();
  }, []);

  // Load instances on mount
  useEffect(() => {
    dispatch(loadInstances());
  }, [dispatch]);

  // Try to select the last used instance when the instances are loaded
  useEffect(() => {
    if (instances?.length) {
      const instance = instances.length === 1 ? instances[0] : null;

      if (instance) {
        const tenant = {
          instance: instance.instance_id,
          region: instance.region
        };

        dispatch(updateTenant(tenant.region, tenant.instance));

        history.push(urlJoin(encodeURIComponent(tenant.instance), 'home'));
      }
    }
  }, [dispatch, instances]);

  return (
    <div role="main" id="instances" className="InstancesPage">
      <InstancesComponent
        instances={actualInstances}
        loading={loading}
        updating={updating}
      />
    </div>
  );
};

export default InstancesPage;
